import { Competency_Map_Status, CompetencyMapQuery } from "@generated/graphql";
import { COMPETENCY_MAP_STATUS_TITLE } from "constants/global";
import { CompetencyMapSelectedParts } from "./competencyMapSelectParts";

type CompetencyMap = Pick<NonNullable<CompetencyMapQuery["competencyMap"]>, "id" | "status" | "competences"> | null;
type Competences = NonNullable<CompetencyMap>["competences"];
type KnowledgeBlockCompetences = NonNullable<NonNullable<Competences>[number]["knowledgeBlockCompetences"]>;
type Skills = NonNullable<NonNullable<KnowledgeBlockCompetences>[number]["knowledgeBlock"]["skills"]>;

const getSkillsError = (skills: Skills) => {
  const emptySkill = skills?.find(
    (skill) => !skill.questions?.length || skill.questions.every((question) => !question.confirmation)
  );

  if (emptySkill) {
    return `Невозможно создать тестирование, так как навык «${emptySkill.title}» не содержит подтвержденных вопросов.`;
  }

  return false;
};

const getKnowledgeBlockCompetencesError = (knowledgeBlockCompetences: KnowledgeBlockCompetences) => {
  for (const knowledgeBlockCompetency of knowledgeBlockCompetences) {
    if (!knowledgeBlockCompetency.knowledgeBlock.skills?.length) {
      return [
        "Невозможно создать тестирование, так как блок знаний",
        `«${knowledgeBlockCompetency.knowledgeBlock.title}» не содержит навыков.`,
      ].join(" ");
    }

    const skillsError = getSkillsError(knowledgeBlockCompetency.knowledgeBlock.skills);
    if (skillsError) return skillsError;
  }

  return false;
};

const getCompetencesError = (competences: Competences) => {
  if (!competences?.length) {
    return "Невозможно создать тестирование, так как карта компетенций пустая.";
  }

  for (const competency of competences) {
    if (!competency.knowledgeBlockCompetences?.length) {
      return `Невозможно создать тестирование, так как компетенция «${competency.title}» не содержит блок знаний.`;
    }

    const knowledgeBlockCompetencesError = getKnowledgeBlockCompetencesError(competency.knowledgeBlockCompetences);
    if (knowledgeBlockCompetencesError) return knowledgeBlockCompetencesError;
  }

  return false;
};

const getSelectedCompetences = (competences: Competences, selectParts?: CompetencyMapSelectedParts): Competences => {
  if (!competences || !selectParts) return competences;

  const competencyIds = new Set(selectParts.competencyIds || []);
  const knowledgeBlockIds = new Set(selectParts.knowledgeBockIds || []);
  const questionIds = new Set(selectParts.questionIds || []);
  const skillIds = new Set(selectParts.skillIds || []);
  const hasQuestionSelection = questionIds.size > 0;

  return competences.reduce<NonNullable<Competences>>((competencesAcc, competency) => {
    const isCompetencySelected = competencyIds.has(competency.id);

    const selectedKnowledgeBlockCompetences =
      competency.knowledgeBlockCompetences?.reduce<NonNullable<KnowledgeBlockCompetences>>(
        (knowledgeBlocksAcc, knowledgeBlockCompetency) => {
          const { knowledgeBlock } = knowledgeBlockCompetency;
          const isKnowledgeBlockSelected = isCompetencySelected || knowledgeBlockIds.has(knowledgeBlock.id);

          const selectedSkills =
            knowledgeBlock.skills?.reduce<NonNullable<NonNullable<typeof knowledgeBlock.skills>>>(
              (skillsAcc, skill) => {
                const isSkillSelected = isKnowledgeBlockSelected || skillIds.has(skill.id);
                const selectedQuestions = hasQuestionSelection
                  ? skill.questions?.filter((question) => questionIds.has(question.id)) || []
                  : skill.questions || [];
                const hasSelectedQuestions = hasQuestionSelection && selectedQuestions.length > 0;

                if (!isSkillSelected && !hasSelectedQuestions) {
                  return skillsAcc;
                }

                skillsAcc.push({
                  ...skill,
                  questions: isSkillSelected && !hasQuestionSelection ? skill.questions : selectedQuestions,
                });

                return skillsAcc;
              },
              []
            ) || [];

          if (!isKnowledgeBlockSelected && !selectedSkills.length) {
            return knowledgeBlocksAcc;
          }

          knowledgeBlocksAcc.push({
            ...knowledgeBlockCompetency,
            knowledgeBlock: {
              ...knowledgeBlock,
              skills: selectedSkills,
            },
          });

          return knowledgeBlocksAcc;
        },
        []
      ) || [];

    if (!isCompetencySelected && !selectedKnowledgeBlockCompetences.length) {
      return competencesAcc;
    }

    competencesAcc.push({
      ...competency,
      knowledgeBlockCompetences: selectedKnowledgeBlockCompetences,
    });

    return competencesAcc;
  }, []);
};

// eslint-disable-next-line import/prefer-default-export
export const getCompetencyMapError = (competencyMap?: CompetencyMap, selectParts?: CompetencyMapSelectedParts) => {
  if (!competencyMap?.id) {
    return "Отсутствует id карты компетенций";
  }

  const { competences, status } = competencyMap;
  if (status !== Competency_Map_Status.Approved) {
    const statusLabel = COMPETENCY_MAP_STATUS_TITLE[status];
    return `Невозможно создать тестирование, так как карта компетенций имеет статус ${statusLabel}`;
  }

  return getCompetencesError(getSelectedCompetences(competences, selectParts));
};
